import { AuthTokens } from "app/types/auth-tokens";

export const ONE_HOUR = 3600000 as const;
export const TWO_HOURS = ONE_HOUR * 2;
export const ONE_MOUNTH = TWO_HOURS * 365;
export const AUTH_TOKENS_CHANGED_EVENT = "authTokensChanged";

class TokenService {
  static storageName: string = "authTokens";
  private disconnectFn: () => void = () => {};

  private notifyTokenChange(): void {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(AUTH_TOKENS_CHANGED_EVENT));
    }
  }

  get tokens(): AuthTokens | null {
    const storedTokens = localStorage.getItem(TokenService.storageName);

    if (storedTokens) {
      try {
        const { tokenData, savedAt } = JSON.parse(storedTokens);
        const age = Date.now() - savedAt;
        const isExpired = age > ONE_MOUNTH;

        if (isExpired) {
          console.warn('[TokenService] Token expired:', {
            savedAt,
            ageInMs: age,
            ageInDays: (age / (1000 * 60 * 60 * 24)).toFixed(2),
            expiryThreshold: ONE_MOUNTH,
            expiryThresholdInDays: (ONE_MOUNTH / (1000 * 60 * 60 * 24)).toFixed(2)
          });
          this.handleTokenExpiry();
          return null;
        }

        return tokenData;
      } catch (error) {
        console.error("[TokenService] Failed to parse stored tokens:", error);
        this.removeToken();
      }
    } else {
      console.warn('[TokenService] No tokens found in localStorage');
    }

    return null;
  }

  setTokens(tokenData: AuthTokens): void {
    const tokenWithMeta = {
      tokenData,
      savedAt: Date.now(),
    };
    localStorage.setItem(
      TokenService.storageName,
      JSON.stringify(tokenWithMeta)
    );
    this.notifyTokenChange();
  }

  removeToken(): void {
    localStorage.removeItem(TokenService.storageName);
    this.notifyTokenChange();
  }

  handleTokenExpiry(): void {
    this.removeToken();
    localStorage.removeItem("signature");
    if (this.disconnectFn) {
      this.disconnectFn();
    }
  }

  registerDisconnectFn(disconnectFn: () => void): void {
    this.disconnectFn = disconnectFn;
  }

  isTokenExpiringSoon(): boolean {
    const storedTokens = localStorage.getItem(TokenService.storageName);

    if (storedTokens) {
      const { savedAt } = JSON.parse(storedTokens);
      return Date.now() - savedAt > ONE_MOUNTH;
    }

    return false;
  }
}

const tokenService = new TokenService();
export default tokenService;
